import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { ScrollArea, Stack } from '@mantine/core';

import { useAuthOptional } from '@/contexts/AuthContext';
import { useAnalystRoster } from '@/hooks/useAnalystRoster';
import { useStickToBottom } from '@/hooks/useStickToBottom';
import type {
  RegistryOrderConversation,
  RegistryOrderReplyRequest,
} from '@/types/registry-documents';
import { conversationEntries } from './conversationEntries';
import { ConversationOrders } from './ConversationOrders';
import { replyTargetsOf, resolveReplyTarget } from './conversationTargets';
import { ConversationEntryView } from './RegistryChatEntry';
import { RegistryReplyComposer } from './RegistryReplyComposer';
import classes from './RegistryConversation.module.css';

interface RegistryChatPanelProps {
  reportId: string;
  conversation: RegistryOrderConversation;
  /** Send a reply to one of the research desk's emails, then re-read.
   * Throws on failure: the composer shows it and keeps the text. */
  onReply: (orderId: string, request: RegistryOrderReplyRequest) => Promise<unknown>;
  /** What the composer answers first, as a deep link named it: a target's
   * key, or an order id for the latest email on that order still waiting.
   * A later value points the composer again. */
  initialTarget?: string | null;
}

/**
 * Armadillo's conversation laid out like the Client chat: the orders it is
 * about as chips above, the thread scrolling between (pinned to its end
 * as new mail lands, unless the reader has scrolled up), and the composer
 * inline at the bottom. Sides go by direction: the desk's emails on the
 * left, the Hub's replies on the right, whoever sent them.
 */
export function RegistryChatPanel({
  reportId,
  conversation,
  onReply,
  initialTarget = null,
}: Readonly<RegistryChatPanelProps>) {
  // The email the composer answers: what a deep link or a Reply link
  // named, by key (a re-read may drop the email it named); null for the
  // conversation's default.
  const [chosenKey, setChosenKey] = useState<string | null>(initialTarget);
  useEffect(() => {
    if (initialTarget) {
      setChosenKey(initialTarget);
    }
  }, [initialTarget]);

  const targets = useMemo(() => replyTargetsOf(conversation), [conversation]);
  const target = resolveReplyTarget(targets, chosenKey);

  // "You" for the viewer's own replies; a colleague's name from the
  // roster, read only once such a reply is on screen. The auth user id
  // arrives from Django as an integer; the message records it as a
  // string, so the two are compared as strings.
  const auth = useAuthOptional();
  const me = auth?.user?.id != null ? String(auth.user.id) : null;
  const colleagueReplied = conversation.items.some(
    (item) => item.message?.direction === 'outbound' && item.message.sentByUserId !== me
  );
  const members = useAnalystRoster(colleagueReplied);
  const authorName = useCallback(
    (userId: string | null): string => {
      if (userId !== null && userId === me) {
        return 'You';
      }
      const member = userId === null ? undefined : members.find((m) => String(m.id) === userId);
      return member?.name ?? 'A colleague';
    },
    [me, members]
  );
  const entries = useMemo(
    () => conversationEntries(conversation, authorName),
    [conversation, authorName]
  );

  const scrollRef = useRef<HTMLDivElement>(null);
  useStickToBottom(scrollRef, entries.length, reportId);

  return (
    <>
      <ConversationOrders orders={conversation.orders} />
      <ScrollArea viewportRef={scrollRef} className={classes.thread} type="auto">
        <Stack gap={4} role="list" aria-label="Armadillo conversation">
          {entries.map((entry) => (
            <ConversationEntryView
              key={entry.key}
              entry={entry}
              onReply={conversation.replyEnabled ? setChosenKey : undefined}
            />
          ))}
        </Stack>
      </ScrollArea>
      <RegistryReplyComposer
        replyEnabled={conversation.replyEnabled}
        targets={targets}
        target={target}
        onTargetChange={setChosenKey}
        onReply={onReply}
        onSent={() => setChosenKey(null)}
      />
    </>
  );
}
